/**
 * Thinking Level Configuration
 *
 * Four-tier thinking system for extended reasoning:
 * - OFF: No extended thinking (0 tokens)
 * - Think: Standard reasoning (moderate token budget)
 * - XHigh Think: Extended reasoning for coding and agentic work
 * - Max Think: Deep reasoning (maximum token budget)
 *
 * Session-level setting with workspace defaults.
 *
 * NOTE: As of `@anthropic-ai/claude-agent-sdk` 0.3.x the SDK emits the
 * adaptive thinking shape (`thinking: { type: "adaptive", display: "summarized" }`
 * + `effort: ...`) natively for Opus 4.7/4.8. The interceptor used to
 * rewrite legacy `enabled` payloads as a compatibility shim; that shim
 * was removed in Phase 3 of the 0.3 upgrade.
 */

export type ThinkingLevel = 'off' | 'think' | 'xhigh' | 'max';

/** Provider-native effort used by managed completion and implementation profiles. */
export type ReasoningEffort = 'low' | 'medium' | 'high' | 'xhigh';

export interface ThinkingLevelDefinition {
  id: ThinkingLevel;
  name: string;
  description: string;
}

/**
 * Available thinking levels with display metadata.
 * Used in UI dropdowns and for validation.
 *
 * Labels are user-facing and should be consistent across all UI surfaces
 * (model dropdown, workspace settings, etc.)
 */
export const THINKING_LEVELS: readonly ThinkingLevelDefinition[] = [
  { id: 'off', name: 'No Thinking', description: 'Fastest responses, no reasoning' },
  { id: 'think', name: 'Thinking', description: 'Balanced speed and reasoning' },
  { id: 'xhigh', name: 'Extra Thinking', description: 'Extended reasoning for coding and agentic work' },
  { id: 'max', name: 'Max Thinking', description: 'Deepest reasoning for complex tasks' },
] as const;

/** Default thinking level for new sessions when workspace has no default */
export const DEFAULT_THINKING_LEVEL: ThinkingLevel = 'xhigh';

/**
 * Token budgets per model family.
 *
 * Haiku max is 8k per Anthropic docs.
 * Sonnet/Opus can use up to 128k, but Anthropic recommends ≤32k for real-time use
 * (above 32k, batch processing is suggested to avoid timeouts).
 * Also, budget_tokens must be < max_tokens, so 64k leaves no room for response.
 *
 * "Think" level matches Claude Code's `think` trigger word budget.
 * "Max Think" is the recommended max for real-time streaming.
 *
 * Note: Token budgets are only used for models that support manual thinking
 * (budget_tokens). Opus 4.7 and 4.8 use adaptive thinking where effort controls depth.
 */
const TOKEN_BUDGETS = {
  haiku: {
    off: 0,
    think: 4_000,
    xhigh: 6_000,
    max: 8_000,
  },
  default: {
    off: 0,
    think: 10_000,
    xhigh: 24_000,
    max: 32_000,
  },
} as const;

/**
 * Get the thinking token budget for a given level and model.
 *
 * @param level - The thinking level (off, think, xhigh, max)
 * @param modelId - The model ID (e.g., 'claude-haiku-4-5-20251001')
 * @returns Number of thinking tokens to allocate
 */
export function getThinkingTokens(level: ThinkingLevel, modelId: string): number {
  const isHaiku = modelId.toLowerCase().includes('haiku');
  const budgets = isHaiku ? TOKEN_BUDGETS.haiku : TOKEN_BUDGETS.default;
  return budgets[level];
}

/**
 * Get display name for a thinking level.
 */
export function getThinkingLevelName(level: ThinkingLevel): string {
  const def = THINKING_LEVELS.find((l) => l.id === level);
  return def?.name ?? level;
}

/**
 * Validate that a value is a valid ThinkingLevel.
 */
export function isValidThinkingLevel(value: unknown): value is ThinkingLevel {
  return value === 'off' || value === 'think' || value === 'xhigh' || value === 'max';
}

/**
 * Map thinking level to SDK effort level.
 *
 * Used with `thinking: { type: 'adaptive' }` to control reasoning depth.
 * - off → 'low' (minimal reasoning)
 * - think → 'high' (default balanced reasoning)
 * - xhigh → 'xhigh' (extended reasoning for coding/agentic work, Opus 4.7 and 4.8 — recommended default)
 * - max → 'max' (deepest reasoning)
 */
export function getEffortLevel(level: ThinkingLevel): 'low' | 'high' | 'xhigh' | 'max' {
  switch (level) {
    case 'off':
      return 'low';
    case 'think':
      return 'high';
    case 'xhigh':
      return 'xhigh';
    case 'max':
      return 'max';
  }
}
